import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import React, { ComponentType } from "react";
import { Box, styled, Typography } from "@mui/material";
import PageLoader from "../PageLoader/PageLoader";
import { TBS_TENANT_ID } from "../../config";

// The sibling of `AdminGuard` for the Tiendas Biai screens (plan §6.3).
//
// **It is a UX affordance, and the server remains the enforcement.** Every TBS route is gated in the
// Lambda router before any handler runs, and RLS scopes the data underneath that. This exists so a
// cashier is told "no" by a page rather than by a failed request — not so that anything is safe.
//
// It reads the same `tbs_tenants` claim the API's authorizer does (§6.1), so the two agree by
// construction. A user whose token carries no memberships sees the refusal, which is the correct
// answer before the Post-Login Action is in place: no membership means no access, everywhere.

const NO_PERMISSION_TEXT = "Ups... parece que no tienes permisos para acceder a esta página.";

const CenteredContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
});

const NoPermissionText = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
}));

const PageLayoutWrapper = styled(Box)({ className: "page-layout" });

type TbsRole = "viewer" | "cashier" | "admin";

/** §6.3: three roles, and they nest. Ranks rather than sets, so a new capability does not require
 *  editing every role's list — the same shape `tbs-common` uses server-side. */
const RANK: Record<string, number> = { viewer: 1, cashier: 2, admin: 3 };

type Membership = { id?: string; slug?: string; roles?: string[] };

/**
 * The roles the caller holds **at the company this build acts for**.
 *
 * Never the union across memberships: someone who is an admin at one company and a cashier at
 * another must not be shown admin screens for the second. The server refuses either way, but a UI
 * that offers the button and then fails is worse than one that does not offer it.
 */
export const rolesForTenant = (memberships: unknown, tenantId: string): string[] => {
  if (!Array.isArray(memberships)) return [];
  const entries = memberships as Membership[];

  // With one membership there is nothing to choose, which matches what the authorizer does when no
  // `X-Tenant-Id` is sent.
  const match = tenantId
    ? entries.find((m) => m.id === tenantId)
    : entries.length === 1
      ? entries[0]
      : undefined;

  return Array.isArray(match?.roles) ? (match?.roles as string[]) : [];
};

export const hasTbsRole = (memberships: unknown, tenantId: string, required: TbsRole): boolean => {
  const needed = RANK[required];
  return rolesForTenant(memberships, tenantId).some((role) => (RANK[role] ?? 0) >= needed);
};

interface TbsGuardProps {
  component: ComponentType;
  /**
   * The least privileged role that may open this screen. Mirrors the route's `x-role`.
   *
   * Named `requiredRole` rather than `role`, which is what plan §6.3 sketched: `role` on a JSX
   * element is the ARIA attribute, `jsx-a11y/aria-role` rejects `role="cashier"` as an invalid one,
   * and the rule is right that the two meanings should not share a name.
   */
  requiredRole?: TbsRole;
}

export const TbsGuard: React.FC<TbsGuardProps> = ({ component, requiredRole = "cashier" }) => {
  const { user } = useAuth0();

  if (user && !hasTbsRole(user.tbs_tenants, TBS_TENANT_ID, requiredRole)) {
    return (
      <CenteredContainer>
        <NoPermissionText variant="h2" gutterBottom>
          {NO_PERMISSION_TEXT}
        </NoPermissionText>
      </CenteredContainer>
    );
  }

  const Component = withAuthenticationRequired(component, {
    onRedirecting: () => (
      <PageLayoutWrapper className="page-layout">
        <PageLoader />
      </PageLayoutWrapper>
    ),
  });

  return <Component />;
};
